<template>
  <div class="relative" ref="buttonRef">
    <Button
      v-if="!hideTrigger"
      text
      rounded
      class="min-h-8 min-w-8 flex-shrink-0 text-[var(--p-primary-color)] rounded-full p-0.5!"
      v-tooltip.top="$t('general.emojis')"
      @click="togglePicker"
    >
      <IconEmoji
        height="18"
        width="18"
        class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]"
      />
    </Button>

    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="showPicker"
          ref="draggableRef"
          data-emoji-picker
          class="fixed z-[9999] bg-white dark:bg-[var(--p-primary-950)] rounded-xl shadow-xl border border-gray-200 dark:border-[var(--p-primary-400)]/30 p-2 flex flex-col transition-all duration-200 ease-out"
          :class="{
            'shadow-2xl scale-105': isDragging,
            'shadow-lg': !isDragging,
          }"
          :style="[pickerSizeStyle, pickerPosition]"
          @click.stop
        >
          <!-- HANDLE PARA ARRASTRAR -->
          <div
            class="flex items-center justify-center py-1 px-2 mb-2 rounded-t-lg cursor-grab active:cursor-grabbing bg-gray-100 dark:bg-[var(--p-primary-900)]/40"
            @mousedown="onMouseDown"
          >
            <div class="w-10 h-1 bg-gray-400 dark:bg-[var(--p-primary-200)]/50 rounded-full"></div>
          </div>

          <!-- CATEGORÍAS -->
          <div class="flex items-center justify-between gap-2 mb-1">
            <PrimeButton
              type="button"
              variant="outlined"
              class="w-8 h-7 rounded-lg transition-all"
              @click="changeCategory('emoticons')"
            >
              <IconSmile class="fill-[var(--p-primary-color)]" />
            </PrimeButton>
            <PrimeButton
              type="button"
              variant="outlined"
              class="w-8 h-7 rounded-lg transition-all"
              @click="changeCategory('animales')"
            >
              <IconTeddy class="stroke-[var(--p-primary-color)]" />
            </PrimeButton>
            <PrimeButton
              type="button"
              variant="outlined"
              class="w-8 h-7 rounded-lg transition-all"
              @click="changeCategory('comida')"
            >
              <IconCup class="fill-[var(--p-primary-color)]" />
            </PrimeButton>
            <PrimeButton
              type="button"
              variant="outlined"
              class="w-8 h-7 rounded-lg transition-all"
              @click="changeCategory('actividades')"
            >
              <IconSoccer class="stroke-[var(--p-primary-color)]" />
            </PrimeButton>
            <PrimeButton
              type="button"
              variant="outlined"
              class="w-8 h-7 rounded-lg transition-all"
              @click="changeCategory('objetos')"
            >
              <IconBulb class="stroke-[var(--p-primary-color)]" />
            </PrimeButton>
            <PrimeButton
              type="button"
              variant="outlined"
              class="w-8 h-7 rounded-lg transition-all"
              @click="changeCategory('viajes')"
            >
              <IconCar class="stroke-[var(--p-primary-color)]" />
            </PrimeButton>
            <PrimeButton
              type="button"
              variant="outlined"
              class="w-8 h-7 rounded-lg transition-all"
              @click="changeCategory('simbolos')"
            >
              <IconHastag class="fill-[var(--p-primary-color)]" />
            </PrimeButton>
            <i class="pi pi-times text-gray-500 cursor-pointer" @click.stop="hidePicker"></i>
          </div>

          <!-- BARRA DE BÚSQUEDA -->
          <div class="relative mb-2 mt-1">
            <AppInput
              v-model="searchTerm"
              type="text"
              :show-clear="true"
              :placeholder="t('general.search_emojis')"
            >
              <template #icon>
                <Search :size="14" class="text-[var(--p-primary-color)]" />
              </template>
            </AppInput>
          </div>

          <!-- GRILLA DE EMOJIS -->
          <div class="overflow-y-auto flex-grow">
            <div v-if="displayedEmojis.length === 0" class="text-center py-8 text-gray-500">--</div>
            <div v-else class="grid grid-cols-10">
              <button
                v-for="emoji in displayedEmojis"
                type="button"
                :key="emoji.id"
                @pointerdown.prevent
                @click="selectEmoji(emoji)"
                class="text-2xl rounded-lg hover:bg-gray-100 dark:hover:bg-[var(--p-primary-500)]/50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300"
                :title="getEmojiKeywords(emoji).join(', ')"
              >
                {{ emoji.emoji }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'

import PrimeButton from 'primevue/button'

import { Search } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

import IconBulb from '@/assets/svg/emojis/bulb.svg?component'
import IconCar from '@/assets/svg/emojis/car.svg?component'
import IconCup from '@/assets/svg/emojis/cup.svg?component'
import IconHastag from '@/assets/svg/emojis/hastag.svg?component'
import IconSmile from '@/assets/svg/emojis/smile.svg?component'
import IconSoccer from '@/assets/svg/emojis/soccer.svg?component'
import IconTeddy from '@/assets/svg/emojis/teddy.svg?component'
import Button from 'primevue/button'
import IconEmoji from '@/assets/svg/emoji_icon.svg?component'
import AppInput from '@/components/atoms/inputs/AppInput.vue'

import { type EmojiItem, type EmojiLocale, emojis } from './emoji.ts'

const { locale, t } = useI18n()

const currentEmojiLocale = computed<EmojiLocale>(() => {
  return locale.value.startsWith('es') ? 'es' : 'en'
})

function getEmojiKeywords(emoji: EmojiItem) {
  return emoji.keywords[currentEmojiLocale.value] ?? emoji.keywords.en
}

interface EmojiCategory {
  key: string
  icon: string
  emojis: EmojiItem[]
}

const props = defineProps<{
  position?: 'top' | 'bottom' | 'left' | 'right'
  hideTrigger?: boolean
}>()

const emit = defineEmits<{
  (e: 'emoji', emoji: string): void
}>()

const showPicker = ref(false)
const searchTerm = ref('')
const selectedCategory = ref('emoticons')
const buttonRef = ref<HTMLElement | null>(null)
const draggableRef = ref<HTMLElement | null>(null)
const isDragging = ref(false)
const offsetX = ref(0)
const offsetY = ref(0)
const hasBeenDragged = ref(false)
const currentPosition = ref({ x: 0, y: 0 })
const viewportSize = ref({ width: 0, height: 0 })
const externalAnchorRect = ref<DOMRect | null>(null)
const externalAnchorElement = ref<HTMLElement | null>(null)

const PICKER_WIDTH = 360
const PICKER_HEIGHT = 450
const VIEWPORT_MARGIN = 8

const categories = ref<EmojiCategory[]>([
  { key: 'emoticons', icon: 'pi pi-andriod', emojis: emojis.emoticons },
  { key: 'animales', icon: 'pi pi-paw', emojis: emojis.animales },
  { key: 'comida', icon: 'pi pi-apple', emojis: emojis.comida },
  { key: 'actividades', icon: 'pi pi-star', emojis: emojis.actividades },
  { key: 'objetos', icon: 'pi pi-box', emojis: emojis.objetos },
  { key: 'viajes', icon: 'pi pi-send', emojis: emojis.viajes },
  { key: 'simbolos', icon: 'pi pi-hashtag', emojis: emojis.simbolos },
])

const allEmojis = computed(() => categories.value.flatMap((cat) => cat.emojis))

const displayedEmojis = computed(() => {
  const term = normalizeSearchValue(searchTerm.value)

  if (term) {
    return allEmojis.value.filter((emoji) =>
      getEmojiKeywords(emoji).some((keyword) => normalizeSearchValue(keyword).includes(term)),
    )
  }

  const category = categories.value.find((c) => c.key === selectedCategory.value)
  return category ? category.emojis : []
})

function normalizeSearchValue(value: string) {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

const pickerSizeStyle = computed(() => ({
  width: `${getPickerWidth()}px`,
  maxHeight: `${getPickerHeight()}px`,
}))

const pickerPosition = computed(() => {
  if (hasBeenDragged.value) {
    const clamped = clampPosition(currentPosition.value.x, currentPosition.value.y)
    return { left: `${clamped.x}px`, top: `${clamped.y}px` }
  }

  const anchorRect = externalAnchorRect.value
  if (anchorRect) {
    const pos = getAnchoredPosition(anchorRect)
    return { left: `${pos.x}px`, top: `${pos.y}px` }
  }

  const el = buttonRef.value as HTMLElement | null
  if (!el) return {}

  const pos = getAnchoredPosition(el.getBoundingClientRect())
  return { left: `${pos.x}px`, top: `${pos.y}px` }
})

function togglePicker() {
  externalAnchorElement.value = null
  externalAnchorRect.value = null
  showPicker.value = !showPicker.value
  if (showPicker.value) {
    hasBeenDragged.value = false
    const el = buttonRef.value as HTMLElement | null
    if (el) {
      currentPosition.value = getAnchoredPosition(el.getBoundingClientRect())
    }
  }
}

function togglePickerFromElement(anchorElement?: HTMLElement) {
  externalAnchorElement.value = anchorElement ?? null
  externalAnchorRect.value = anchorElement?.getBoundingClientRect() ?? null
  showPicker.value = !showPicker.value
  if (showPicker.value) {
    hasBeenDragged.value = false
    if (externalAnchorRect.value) {
      currentPosition.value = getAnchoredPosition(externalAnchorRect.value)
    }
  }
}

function hidePicker() {
  showPicker.value = false
}

function getPickerWidth() {
  return Math.min(PICKER_WIDTH, Math.max(0, viewportSize.value.width - VIEWPORT_MARGIN * 2))
}

function getPickerHeight() {
  return Math.min(PICKER_HEIGHT, Math.max(0, viewportSize.value.height - VIEWPORT_MARGIN * 2))
}

function clampPosition(x: number, y: number) {
  const maxX = window.innerWidth - getPickerWidth() - VIEWPORT_MARGIN
  const maxY = window.innerHeight - getPickerHeight() - VIEWPORT_MARGIN

  return {
    x: Math.max(VIEWPORT_MARGIN, Math.min(x, maxX)),
    y: Math.max(VIEWPORT_MARGIN, Math.min(y, maxY)),
  }
}

function getAnchoredPosition(rect: DOMRect) {
  const gap = 8
  const width = getPickerWidth()
  const height = getPickerHeight()

  const positions = {
    top: { x: rect.left, y: rect.top - height - gap },
    bottom: { x: rect.left, y: rect.bottom + gap },
    left: { x: rect.left - width - gap, y: rect.top },
    right: { x: rect.right + gap, y: rect.top },
  }

  const requestedPosition = props.position || 'bottom'
  let pos = positions[requestedPosition]

  if (requestedPosition === 'bottom' && pos.y + height > window.innerHeight - VIEWPORT_MARGIN) {
    pos = positions.top
  }

  if (requestedPosition === 'top' && pos.y < VIEWPORT_MARGIN) {
    pos = positions.bottom
  }

  if (requestedPosition === 'right' && pos.x + width > window.innerWidth - VIEWPORT_MARGIN) {
    pos = positions.left
  }

  if (requestedPosition === 'left' && pos.x < VIEWPORT_MARGIN) {
    pos = positions.right
  }

  return clampPosition(pos.x, pos.y)
}
function clearSearch() {
  searchTerm.value = ''
}

function changeCategory(category: string) {
  selectedCategory.value = category
  searchTerm.value = ''
}

function selectEmoji(emoji: EmojiItem) {
  emit('emoji', emoji.emoji)
}

const handleClickOutside = (event: PointerEvent) => {
  if (!showPicker.value || !(event.target instanceof Node)) return
  if (buttonRef.value?.contains(event.target)) return
  if (draggableRef.value?.contains(event.target)) return
  if (externalAnchorElement.value?.contains(event.target)) return
  hidePicker()
}

const onMouseDown = (e: MouseEvent) => {
  if (!draggableRef.value) return
  isDragging.value = true
  e.preventDefault()

  const rect = draggableRef.value.getBoundingClientRect()
  offsetX.value = e.clientX - rect.left
  offsetY.value = e.clientY - rect.top

  if (draggableRef.value) {
    draggableRef.value.style.transition = 'none'
  }

  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}

const onMouseMove = (e: MouseEvent) => {
  if (!isDragging.value || !draggableRef.value) return
  hasBeenDragged.value = true

  let x = e.clientX - offsetX.value
  let y = e.clientY - offsetY.value

  const maxX = window.innerWidth - draggableRef.value.offsetWidth - VIEWPORT_MARGIN
  const maxY = window.innerHeight - draggableRef.value.offsetHeight - VIEWPORT_MARGIN

  x = Math.max(VIEWPORT_MARGIN, Math.min(x, maxX))
  y = Math.max(VIEWPORT_MARGIN, Math.min(y, maxY))

  currentPosition.value = { x, y }

  draggableRef.value.style.left = `${x}px`
  draggableRef.value.style.top = `${y}px`
  draggableRef.value.style.right = 'auto'
  draggableRef.value.style.bottom = 'auto'
}

const onMouseUp = () => {
  isDragging.value = false
  if (draggableRef.value) {
    draggableRef.value.style.transition = 'all 0.2s ease-out'
  }
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
}

const updateViewportSize = () => {
  viewportSize.value = {
    width: window.innerWidth,
    height: window.innerHeight,
  }
}

onMounted(() => {
  updateViewportSize()
  document.addEventListener('pointerdown', handleClickOutside, true)
  window.addEventListener('resize', updateViewportSize)
})

onUnmounted(() => {
  document.removeEventListener('pointerdown', handleClickOutside, true)
  window.removeEventListener('resize', updateViewportSize)
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
})

defineExpose({
  togglePickerFromElement,
  hidePicker,
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.2s,
    transform 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
.p-button {
  padding: 4px;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
